/**
 * Tenant Store
 * 
 * Manages multi-tenancy state:
 * - Current tenant and workspace context
 * - Tenant and workspace CRUD
 * - Membership management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { 
  Tenant, 
  Workspace, 
  TenantMembership,
  WorkspaceMembership,
  TenantRole,
  WorkspaceRole,
} from '../../types/prioritization/tenant';
import {
  insertRecord,
  updateRecord,
  fromDbFormat,
  isSupabaseAvailable,
  getSafeSupabaseClient,
  generateUUID,
  setCurrentWorkspaceIdHint,
} from '../../lib/supabaseSync';
import { updateTenantContextWorkspace } from '../../lib/tenantContext';
import {
  useSessionPolicyStore,
  decorateWorkspaceWithPolicy,
} from './sessionPolicyStore';
import { elevateAdmin } from '../../lib/delegationRpc';
import { defaultPlan, type WorkspacePlanRecord } from '../../lib/entitlements';
import { hydrateWorkspacePlanCache } from '../../lib/ai/aiAssistSettings';

/**
 * Rows to Workspaces. Phase C-2 (canon 28 §6, migration 598): workspace session
 * defaults live on session_policies ('workspace' scope); decorate the legacy
 * domain fields so existing readers (effectiveConfigResolver, Defaults.tsx)
 * keep working.
 */
async function workspacesFromRows(rows: unknown[]): Promise<Workspace[]> {
  const workspaces = rows.map((r) => fromDbFormat<Workspace>(r as Record<string, unknown>));
  const policies = await useSessionPolicyStore
    .getState()
    .fetchPoliciesForScopes('workspace', workspaces.map((w) => w.id));
  return workspaces.map((w) => decorateWorkspaceWithPolicy(w, policies.get(w.id)));
}

// ============================================================================
// Store State Type
// ============================================================================

interface TenantStoreState {
  // Current context
  currentTenantId: string | null;
  currentWorkspaceId: string | null;
  /**
   * The user the persisted context was last resolved for. A different
   * signed-in user must never inherit the previous user's workspace scope
   * (live incident 2026-07-27: Erik inherited a stale workspace he wasn't a
   * member of — every list rendered empty and writes failed RLS).
   */
  lastResolvedUserId: string | null;
  /**
   * Workspace ids the current user is a member of; null = unrestricted
   * (super admin) or not yet known (fetch failed) — in both cases selection
   * falls back to the tenant list rather than locking the user out.
   */
  memberWorkspaceIds: string[] | null;

  // Data
  tenants: Tenant[];
  workspaces: Workspace[];
  /**
   * Workspaces the current user belongs to that sit outside any tenant. A
   * personal workspace always does: onboarding creates it with no tenant, so
   * fetchWorkspaces(tenantId) can never return one. Kept in `workspaces` too.
   */
  tenantlessWorkspaces: Workspace[];
  tenantMemberships: TenantMembership[];
  workspaceMemberships: WorkspaceMembership[];
  
  // Commercial plan for the current workspace (null until resolved)
  workspacePlan: WorkspacePlanRecord | null;

  // Loading state
  isLoading: boolean;
  hasFetched: boolean;
  // Set to true once ensureWorkspaceContext() has finished (regardless of whether
  // a workspace was found). Used by the dashboard to distinguish "still resolving"
  // from "resolved but no workspace" — the latter should show the empty state,
  // not an infinite loading skeleton.
  workspaceResolutionComplete: boolean;
  setWorkspaceResolutionComplete: () => void;
  
  // Actions - Context
  setCurrentTenant: (tenantId: string | null) => void;
  setCurrentWorkspace: (workspaceId: string | null) => void;
  switchTenant: (tenantId: string, options?: { keepTenantlessWorkspace?: boolean }) => Promise<void>;
  
  // Actions - Fetch
  fetchTenants: (userId: string) => Promise<void>;
  fetchWorkspaces: (tenantId: string) => Promise<void>;
  fetchWorkspacePlan: (workspaceId: string) => Promise<void>;
  
  // Actions - Tenant CRUD
  createTenant: (params: { name: string; slug: string; description?: string }) => Promise<Tenant | null>;
  updateTenant: (id: string, updates: Partial<Tenant>) => void;
  
  // Actions - Workspace CRUD
  createWorkspace: (params: { tenantId: string; name: string; slug: string; description?: string }) => Promise<Workspace | null>;
  updateWorkspace: (id: string, updates: Partial<Workspace>) => void;
  
  // Actions - Membership
  addTenantMember: (tenantId: string, userId: string, role: TenantRole) => Promise<void>;
  updateTenantMemberRole: (membershipId: string, role: TenantRole) => void;
  addWorkspaceMember: (workspaceId: string, userId: string, role: WorkspaceRole) => Promise<void>;
  
  // Getters
  getCurrentTenant: () => Tenant | undefined;
  getCurrentWorkspace: () => Workspace | undefined;
  getWorkspacesForTenant: (tenantId: string) => Workspace[];
  getUserTenantRole: (userId: string, tenantId: string) => TenantRole | null;
  getUserWorkspaceRole: (userId: string, workspaceId: string) => WorkspaceRole | null;
}

// ============================================================================
// Store
// ============================================================================

export const useTenantStore = create<TenantStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentTenantId: null,
      currentWorkspaceId: null,
      lastResolvedUserId: null,
      memberWorkspaceIds: null,
      workspacePlan: null,
      tenants: [],
      workspaces: [],
      tenantlessWorkspaces: [],
      tenantMemberships: [],
      workspaceMemberships: [],
      isLoading: false,
      hasFetched: false,
      workspaceResolutionComplete: false,
      setWorkspaceResolutionComplete: () => set({ workspaceResolutionComplete: true }),

      setCurrentTenant: (tenantId) => {
        set({ currentTenantId: tenantId, currentWorkspaceId: null });
      },
      
      setCurrentWorkspace: (workspaceId) => {
        set({ currentWorkspaceId: workspaceId });
        // Stage 7 Slice 2: Keep authority (tenant context) in sync when user switches workspace in UI
        if (workspaceId) {
          const ws = get().workspaces.find((w) => w.id === workspaceId);
          updateTenantContextWorkspace(workspaceId, ws?.name);
          setCurrentWorkspaceIdHint(workspaceId);
          // Load commercial plan for the newly-active workspace (fire-and-forget)
          get().fetchWorkspacePlan(workspaceId);
        } else {
          setCurrentWorkspaceIdHint(null);
          set({ workspacePlan: null });
          hydrateWorkspacePlanCache(null);
        }
        // Keep the legacy sessionStorage hint in sync for compatibility only.
        // Workspace authority still comes from demo mode or server-backed tenant context.
        try {
          if (workspaceId) {
            sessionStorage.setItem('peakPriorities_currentWorkspaceId', workspaceId);
          } else {
            sessionStorage.removeItem('peakPriorities_currentWorkspaceId');
          }
        } catch (e) {
          console.warn('[TenantStore] Failed to sync workspace to sessionStorage:', e);
        }
      },
      
      switchTenant: async (tenantId, options) => {
        // Preserve the current workspace ID so the selection logic below can restore it
        // if it's still valid in the new tenant. Setting currentWorkspaceId: null here
        // caused a null→X transition on every init (fetchTenants calls switchTenant on
        // page load), which flickered every dashboard and skeleton view 2-3 times.
        const prevWorkspaceId = get().currentWorkspaceId;
        set({ currentTenantId: tenantId });
        
        // Keep the legacy tenant/workspace hint in sync for compatibility only.
        try {
          sessionStorage.setItem('peakPriorities_currentTenantId', tenantId);
        } catch (e) {
          console.warn('[TenantStore] Failed to sync to sessionStorage:', e);
        }
        
        await get().fetchWorkspaces(tenantId);

        // Resolving the tenant must not move someone out of a workspace with no
        // tenant: it is never in the tenant's list below, so it would read as stale.
        // Someone who picks a tenant in the switcher is moved, as they asked.
        if (
          options?.keepTenantlessWorkspace &&
          get().tenantlessWorkspaces.some((w) => w.id === prevWorkspaceId)
        ) {
          get().setCurrentWorkspace(prevWorkspaceId);
          return;
        }

        // Auto-select first workspace when there's only one, or when none is selected,
        // or when the previously-selected workspace no longer belongs to this tenant.
        // fetchWorkspaces returns EVERY workspace in the tenant, so validating the
        // persisted selection against it kept stale scopes alive for users who
        // weren't members — restrict to membership when we know it.
        const workspaces = get().getWorkspacesForTenant(tenantId);
        if (workspaces.length > 0) {
          const memberIds = get().memberWorkspaceIds;
          const usable =
            memberIds == null ? workspaces : workspaces.filter((w) => memberIds.includes(w.id));
          const pool = usable.length > 0 ? usable : workspaces;
          const currentInList = pool.some((w) => w.id === prevWorkspaceId);
          const workspaceId = pool.length === 1 || !currentInList ? pool[0].id : prevWorkspaceId;
          if (workspaceId) {
            get().setCurrentWorkspace(workspaceId);
          }
        }
      },
      
      fetchTenants: async (userId) => {
        if (!userId || userId === 'unknown') {
          set({ hasFetched: false });
          return;
        }
        if (!isSupabaseAvailable()) {
          set({ hasFetched: true });
          return;
        }
        
        set({ isLoading: true });

        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          set({ isLoading: false, hasFetched: true });
          return;
        }

        // Identity switch: drop the previous user's persisted scope before
        // resolving, instead of treating it as a candidate selection.
        const prevUserId = get().lastResolvedUserId;
        if (prevUserId && prevUserId !== userId) {
          console.log('[TenantStore] Signed-in user changed — resetting persisted workspace scope');
          set({ currentTenantId: null, currentWorkspaceId: null, workspacePlan: null });
        }
        set({ lastResolvedUserId: userId });

        try {
          // Fetch tenant memberships for user
          const { data: memberships } = await supabase
            .from('tenant_memberships')
            .select('*')
            .eq('user_id', userId);

          const tenantIdsFromMemberships = new Set<string>((memberships || []).map(m => m.tenant_id));

          // Also include tenants from workspaces the user is in (e.g. workspace-admin with no tenant_membership yet)
          const { data: wsMemberships, error: wsMembershipsError } = await supabase
            .from('workspace_memberships')
            .select('workspace_id')
            .eq('user_id', userId);
          let tenantlessWorkspaces: Workspace[] = [];
          if (wsMemberships && wsMemberships.length > 0) {
            const workspaceIds = wsMemberships.map(m => m.workspace_id);
            const { data: workspaces } = await supabase
              .from('workspaces')
              .select('*')
              .in('id', workspaceIds);
            // A workspace with no tenant adds no tenant: a null in this list fails
            // the tenants read below (22P02), which left anyone with a personal
            // workspace with no tenants and no workspaces.
            const rows = workspaces || [];
            rows.forEach(w => { if (w.tenant_id) tenantIdsFromMemberships.add(w.tenant_id); });
            tenantlessWorkspaces = await workspacesFromRows(
              rows.filter(w => !w.tenant_id && w.status === 'active')
            );
          }
          set((state) => ({
            tenantlessWorkspaces,
            workspaces: [...state.workspaces.filter((w) => w.tenantId), ...tenantlessWorkspaces],
          }));
          
          const allTenantIds = [...tenantIdsFromMemberships];

          // Platform admins always see all tenants, regardless of membership rows.
          let resolvedTenantIds = allTenantIds;
          const { data: superAdminRow } = await supabase
            .from('super_admins')
            .select('id')
            .eq('user_id', userId)
            .maybeSingle();
          if (superAdminRow) {
            const { data: allTenantRows } = await supabase
              .from('tenants')
              .select('id')
              .order('name');
            resolvedTenantIds = (allTenantRows || []).map((r) => r.id);
          }

          // Record membership scope BEFORE switchTenant runs so its workspace
          // selection can honor it. null = unrestricted (super admin) or
          // unknown (fetch failed) — never lock a user out on a transient error.
          set({
            memberWorkspaceIds:
              superAdminRow || wsMembershipsError
                ? null
                : (wsMemberships || []).map((m: { workspace_id: string }) => m.workspace_id),
          });

          if (resolvedTenantIds.length > 0 || tenantlessWorkspaces.length > 0) {
            const { data: tenants } = await supabase
              .from('tenants')
              .select('*')
              .in('id', resolvedTenantIds)
              .order('name');
            
            set({ 
              tenants: tenants || [],
              tenantMemberships: memberships || [],
              isLoading: false,
              hasFetched: true,
            });
            
            const state = get();
            const tenantList = tenants || [];
            const currentInList = tenantList.length > 0 && state.currentTenantId && tenantList.some((t) => t.id === state.currentTenantId);
            // Auto-select when: no selection, or single tenant (always use it), or current selection not in list (stale)
            if (tenantList.length > 0) {
              if (tenantList.length === 1 || !state.currentTenantId || !currentInList) {
                await state.switchTenant(tenantList[0].id, { keepTenantlessWorkspace: true });
              } else if (currentInList) {
                await state.fetchWorkspaces(state.currentTenantId);
              }
            }

            // Guard: if the currently-selected workspace is not in this user's
            // workspace_memberships (e.g. stale workspace from a previous user's
            // session), reset to their first accessible workspace — or to NO
            // workspace when they have none. Keeping a scope the user cannot
            // read or write renders every list empty and every write an RLS
            // refusal; the honest empty state is strictly better.
            // Platform admins (super_admins) may operate in any workspace, and a
            // failed memberships fetch must not clear a valid selection.
            if (!superAdminRow && !wsMembershipsError) {
              const userWsIds = new Set((wsMemberships || []).map((m: { workspace_id: string }) => m.workspace_id));
              const selectedWsId = get().currentWorkspaceId;
              if (selectedWsId && !userWsIds.has(selectedWsId)) {
                const firstAccessible = get().workspaces.find((w) => userWsIds.has(w.id));
                console.log(
                  '[TenantStore] Selected workspace not in user memberships — resetting to',
                  firstAccessible?.id ?? 'none',
                );
                get().setCurrentWorkspace(firstAccessible?.id ?? null);
              }
            }
          } else {
            set({ isLoading: false, hasFetched: true });
          }
        } catch (error) {
          console.error('[TenantStore] Error fetching tenants:', error);
          set({ isLoading: false, hasFetched: true });
        }
      },
      
      fetchWorkspaces: async (tenantId) => {
        if (!isSupabaseAvailable()) return;
        
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        try {
          const { data: rows } = await supabase
            .from('workspaces')
            .select('*')
            .eq('tenant_id', tenantId)
            .eq('status', 'active');
          const workspaces = await workspacesFromRows(rows || []);
          set({ workspaces: [...workspaces, ...get().tenantlessWorkspaces] });
        } catch (error) {
          console.error('[TenantStore] Error fetching workspaces:', error);
        }
      },
      
      fetchWorkspacePlan: async (workspaceId) => {
        if (!isSupabaseAvailable()) return;

        const supabase = getSafeSupabaseClient();
        if (!supabase) return;

        try {
          const { data } = await supabase
            .from('workspace_plans')
            .select('workspace_id, tier, status, add_ons, seats, term_starts_at, term_ends_at')
            .eq('workspace_id', workspaceId)
            .maybeSingle();

          if (data) {
            const plan: WorkspacePlanRecord = {
              workspaceId: data.workspace_id,
              tier: data.tier,
              status: data.status,
              addOns: data.add_ons ?? [],
              seats: data.seats ?? undefined,
              termStartsAt: data.term_starts_at ?? undefined,
              termEndsAt: data.term_ends_at ?? undefined,
            };
            set({ workspacePlan: plan });
            hydrateWorkspacePlanCache(plan);
          } else {
            // No plan row yet: fall back to default pilot/trialing so the UI
            // has something to reason against before the migration is applied.
            const plan = defaultPlan(workspaceId);
            set({ workspacePlan: plan });
            hydrateWorkspacePlanCache(plan);
          }
        } catch (error) {
          console.error('[TenantStore] Error fetching workspace plan:', error);
          const plan = defaultPlan(workspaceId);
          set({ workspacePlan: plan });
          hydrateWorkspacePlanCache(plan);
        }
      },

      createTenant: async (params) => {
        const now = new Date();
        const tenant: Tenant = {
          id: generateUUID(),
          name: params.name,
          slug: params.slug,
          description: params.description,
          settings: {},
          status: 'active',
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          tenants: [...state.tenants, tenant],
        }));
        
        if (isSupabaseAvailable()) {
          const result = await insertRecord('tenants', tenant);
          if (result.error) {
            console.error('[TenantStore] Error creating tenant:', result.error);
            return null;
          }
        }
        
        return tenant;
      },
      
      updateTenant: (id, updates) => {
        const tenant = get().tenants.find(t => t.id === id);
        if (!tenant) return;
        
        const updated = { ...tenant, ...updates, updatedAt: new Date() };
        
        set((state) => ({
          tenants: state.tenants.map(t => t.id === id ? updated : t),
        }));
        
        if (isSupabaseAvailable()) {
          updateRecord('tenants', id, updated);
        }
      },
      
      createWorkspace: async (params) => {
        const now = new Date();
        const workspace: Workspace = {
          id: generateUUID(),
          tenantId: params.tenantId,
          name: params.name,
          slug: params.slug,
          description: params.description,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          workspaces: [...state.workspaces, workspace],
        }));
        
        if (isSupabaseAvailable()) {
          const result = await insertRecord('workspaces', workspace);
          if (result.error) {
            console.error('[TenantStore] Error creating workspace:', result.error);
            return null;
          }
        }
        
        return workspace;
      },
      
      updateWorkspace: (id, updates) => {
        const workspace = get().workspaces.find(w => w.id === id);
        if (!workspace) return;
        
        const updated = { ...workspace, ...updates, updatedAt: new Date() };
        
        set((state) => ({
          workspaces: state.workspaces.map(w => w.id === id ? updated : w),
        }));
        
        if (isSupabaseAvailable()) {
          updateRecord('workspaces', id, updated);
        }
      },
      
      addTenantMember: async (tenantId, userId, role) => {
        const membership: TenantMembership = {
          id: generateUUID(),
          userId,
          tenantId,
          role,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          tenantMemberships: [...state.tenantMemberships, membership],
        }));
        
        if (isSupabaseAvailable()) {
          await insertRecord('tenant_memberships', membership);
        }
      },
      
      updateTenantMemberRole: async (membershipId, role) => {
        const membership = get().tenantMemberships.find(m => m.id === membershipId);
        if (!membership) return;

        if (role === 'admin') {
          const result = await elevateAdmin({
            targetUserId: membership.userId,
            adminRole: 'TENANT_ADMIN',
            scopeId: membership.tenantId,
          });
          if (!result.success) {
            console.warn('[TenantStore] updateTenantMemberRole denied:', result.error);
            return;
          }
        }

        set((state) => ({
          tenantMemberships: state.tenantMemberships.map(m =>
            m.id === membershipId ? { ...m, role, updatedAt: new Date() } : m
          ),
        }));
      },
      
      addWorkspaceMember: async (workspaceId, userId, role) => {
        const adminRole = role === 'admin' ? 'WORKSPACE_ADMIN' : null;
        if (adminRole) {
          const result = await elevateAdmin({
            targetUserId: userId,
            adminRole: 'WORKSPACE_ADMIN',
            scopeId: workspaceId,
          });
          if (!result.success) {
            console.warn('[TenantStore] addWorkspaceMember denied:', result.error);
            return;
          }
        }

        const membership: WorkspaceMembership = {
          id: generateUUID(),
          userId,
          workspaceId,
          role,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          workspaceMemberships: [...state.workspaceMemberships, membership],
        }));
      },
      
      getCurrentTenant: () => {
        const { currentTenantId, tenants } = get();
        return tenants.find(t => t.id === currentTenantId);
      },
      
      getCurrentWorkspace: () => {
        const { currentWorkspaceId, workspaces } = get();
        return workspaces.find(w => w.id === currentWorkspaceId);
      },
      
      getWorkspacesForTenant: (tenantId) => {
        return get().workspaces.filter(w => (w as { tenantId?: string; tenant_id?: string }).tenantId === tenantId || (w as { tenant_id?: string }).tenant_id === tenantId);
      },
      
      getUserTenantRole: (userId, tenantId) => {
        const membership = get().tenantMemberships.find(
          m => m.userId === userId && m.tenantId === tenantId
        );
        return membership?.role || null;
      },
      
      getUserWorkspaceRole: (userId, workspaceId) => {
        const membership = get().workspaceMemberships.find(
          m => m.userId === userId && m.workspaceId === workspaceId
        );
        return membership?.role || null;
      },
    }),
    {
      name: 'tenant-context',
      partialize: (state) => ({
        currentTenantId: state.currentTenantId,
        currentWorkspaceId: state.currentWorkspaceId,
        // Persisted so a NEW signed-in user in the same browser is detected
        // and never inherits this scope (see fetchTenants identity reset).
        lastResolvedUserId: state.lastResolvedUserId,
      }),
      // Keep the legacy sessionStorage hint in sync after rehydration.
      // This is not an authority source.
      onRehydrateStorage: () => (state, _err) => {
        if (state?.currentWorkspaceId && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('peakPriorities_currentWorkspaceId', state.currentWorkspaceId);
        }
      },
    }
  )
);
